/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo } from 'react';
import BrandContext from './BrandContext';
import initialBrandState from './initialBrandState';
import brandReducer from 'contexts/brand/reducer/brand.reducer';
import * as action from 'contexts/brand/reducer/brand.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  IgetBrandActionProps,
  IshareDataWithBrandActionProps,
} from 'interfaces/brand.interface';
import useContextMyBrands from 'hooks/contexts/useContextMyBrands';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';

function BrandProvider(props: ChildrenProps) {
  const [state, dispatch] = useReducer(brandReducer, initialBrandState);
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();
  const { company } = useContextCompany();
  const { myBrands, isLoadingGetMyBrands } = useContextMyBrands();

  const getBrand = useCallback(
    async ({ brandId, tenantId }: IgetBrandActionProps) => {
      if (!company?.id || !company?.country?.id || isLoadingGetMyBrands) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getBrandAction({
        dispatch,
        clientId: company.id,
        brandId,
        tenantId,
        myBrands,
        token,
        translate,
      });
    },
    [company?.id, company?.country?.id, myBrands, isLoadingGetMyBrands]
  );

  const shareDataWithBrand = useCallback(
    async ({ datashareId, value }: IshareDataWithBrandActionProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.shareDataWithBrandAction({
        dispatch,
        datashareId,
        value,
        token,
        translate,
      });
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...state,
      getBrand,
      shareDataWithBrand,
    }),
    [state, getBrand, shareDataWithBrand]
  );

  return (
    <BrandContext.Provider value={memoProvider}>{props.children}</BrandContext.Provider>
  );
}

export default memo(BrandProvider);
